import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

type IntelligenceActionId = "normalize-names" | "validate-zefix";

interface Action {
  id: IntelligenceActionId;
  label: string;
  description: string;
}

const VENDOR_ACTIONS: Action[] = [
  {
    id: "normalize-names",
    label: "Normalize Names",
    description: "Clean up and standardize vendor names",
  },
  {
    id: "validate-zefix",
    label: "Validate via Zefix",
    description: "Match vendors to Swiss company registry",
  },
];

const CLIENT_ACTIONS: Action[] = [
  {
    id: "normalize-names",
    label: "Normalize Names",
    description: "Clean up and standardize client names",
  },
  {
    id: "validate-zefix",
    label: "Validate via Zefix",
    description: "Match clients to Swiss company registry",
  },
];

interface IntelligenceRefineButtonProps {
  entityType: "vendor" | "client";
  sessionId: number;
  onRefineComplete: () => void;
  disabled?: boolean;
}

export function IntelligenceRefineButton({
  entityType,
  sessionId,
  onRefineComplete,
  disabled = false,
}: IntelligenceRefineButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [activeAction, setActiveAction] = useState<IntelligenceActionId | null>(
    null
  );

  const actions = entityType === "vendor" ? VENDOR_ACTIONS : CLIENT_ACTIONS;

  // tRPC mutations
  const normalizeVendorNames =
    trpc.intelligence.normalizeVendorNames.useMutation();
  const normalizeClientNames =
    trpc.intelligence.normalizeClientNames.useMutation();
  const validateVendorsZefix =
    trpc.intelligence.validateVendorsZefix.useMutation();
  const validateClientsZefix =
    trpc.intelligence.validateClientsZefix.useMutation();

  const handleAction = async (actionId: IntelligenceActionId) => {
    setIsLoading(true);
    setActiveAction(actionId);

    try {
      if (entityType === "vendor") {
        switch (actionId) {
          case "normalize-names":
            const normalizeResult = await normalizeVendorNames.mutateAsync({
              sessionId,
            });
            if (normalizeResult.success) {
              toast.success(normalizeResult.message);
            } else {
              toast.info(normalizeResult.message);
            }
            break;

          case "validate-zefix":
            const zefixResult = await validateVendorsZefix.mutateAsync({
              sessionId,
            });
            if (zefixResult.success) {
              toast.success(zefixResult.message);
            } else {
              toast.info(zefixResult.message);
            }
            break;
        }
      } else {
        switch (actionId) {
          case "normalize-names":
            const normalizeResult = await normalizeClientNames.mutateAsync({
              sessionId,
            });
            if (normalizeResult.success) {
              toast.success(normalizeResult.message);
            } else {
              toast.info(normalizeResult.message);
            }
            break;

          case "validate-zefix":
            const zefixResult = await validateClientsZefix.mutateAsync({
              sessionId,
            });
            if (zefixResult.success) {
              toast.success(zefixResult.message);
            } else {
              toast.info(zefixResult.message);
            }
            break;
        }
      }

      onRefineComplete();
    } catch (error) {
      console.error("Refinement error:", error);
      toast.error(error instanceof Error ? error.message : "Refinement failed");
    } finally {
      setIsLoading(false);
      setActiveAction(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled || isLoading}
          className="h-7 px-2 text-muted-foreground hover:text-foreground"
          onClick={e => e.stopPropagation()}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span className="ml-1 text-xs">Refine</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {entityType === "vendor" ? "Vendor" : "Client"} Refinement
        </div>
        <DropdownMenuSeparator />
        {actions.map(action => {
          const isActive = activeAction === action.id;
          const IconComponent =
            action.id === "validate-zefix" ? BadgeCheck : Sparkles;

          return (
            <DropdownMenuItem
              key={action.id}
              onClick={e => {
                e.stopPropagation();
                handleAction(action.id);
              }}
              disabled={isLoading}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <div className="flex items-center gap-2 w-full">
                {isActive ? (
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                ) : (
                  <IconComponent className="h-4 w-4 shrink-0" />
                )}
                <span className="font-medium">{action.label}</span>
              </div>
              <span className="text-xs text-muted-foreground pl-6">
                {action.description}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
